import React from "react";
import PropTypes from "prop-types";
import { createHeadingNavProps } from "../utils/createToc";

/**
 * HeadingWrapper component that provides consistent navigation anchors for headings
 * This ensures that heading IDs and hrefs match exactly with TOC generation
 */
const HeadingWrapper = ({
  text,
  type,
  identifier,
  children,
  className = "",
  style = {},
  wrapperTag = "div",
  display = "block",
  ...props
}) => {
  // Generate consistent navigation props using createToc logic
  const navProps = createHeadingNavProps(text, type, identifier);

  // If no valid link can be created, just render the children
  if (!navProps.hasValidLink) {
    const WrapperTag = wrapperTag;
    return (
      <WrapperTag className={className} style={style} {...props}>
        {children}
      </WrapperTag>
    );
  }

  // Create the wrapper element with proper ID for TOC linking
  const WrapperTag = wrapperTag;

  return (
    <WrapperTag className={className} style={style} {...props}>
      {/* Create an anchor target with the ID that TOC will reference */}
      <a
        id={navProps.id}
        href={navProps.href}
        className="heading-anchor"
        style={{
          textDecoration: "none",
          color: "inherit",
          display: `${display || "block"}`,
          position: "relative",
        }}
        onClick={e => {
          // Prevent default navigation but allow the anchor to work
          // This is mainly for accessibility and consistency
          e.preventDefault();

          // Scroll to this element smoothly
          const element = document.getElementById(navProps.id);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }

          // Update URL hash without causing page jump
          if (window.history && window.history.pushState) {
            window.history.pushState(null, null, navProps.href);
          }
        }}
      >
        {children}
      </a>
    </WrapperTag>
  );
};

HeadingWrapper.propTypes = {
  text: PropTypes.string.isRequired,
  type: PropTypes.oneOf(["chapter", "topic"]).isRequired,
  identifier: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  display: PropTypes.string,
  style: PropTypes.object,
  wrapperTag: PropTypes.string,
};

export default HeadingWrapper;
